import type { Actor as WireActor } from '@ofmos/shared/actor';
import { FIELD_TYPES } from '@ofmos/shared/field-types';
import { isSelectOptionId } from '@ofmos/shared/ids';
import { resolveLabelField } from '@ofmos/shared/label-field';
import { isObjectColor } from '@ofmos/shared/object-color';
import type { RecordEventAction } from '@ofmos/shared/record-write';
import { ICON_NAMES, isDrawingName, type ObjectIcon, TAG_TONES, type TagTone } from '../../ui';
/**
 * THE NAMES ONLY. The drawings behind them are 470 kB of path data, and a module that
 * validates a stored name must not pull them into the data chunk (Codex review, 2026-09-09).
 */
import { isGlyphName } from '../../ui/ObjectGlyph/generated/names';
import type {
  Actor,
  FieldMetadata,
  FieldOptions,
  FieldValue,
  MetadataVisibility,
  ObjectMetadata,
  RecordEvent,
  RecordEventType,
  RecordRow,
  SelectOptionDef,
} from '../client';
import type { WireField, WireObject, WireRecord, WireRecordEvent } from './wire';

/**
 * SERVER JSON -> the UI's types, in pure functions.
 *
 * The two models are close but not identical, and each difference is a decision:
 *
 *  - `labelIdentifierFieldId` is DERIVED here with the shared label-field rule
 *    (`label-field.ts`), because the server stores no such column. One rule, one
 *    implementation - a chip and a search hit that disagreed about a record's title is
 *    the inconsistency that rule exists to prevent.
 *  - `icon` does not exist on the server at all. It is derived from the object's api
 *    name, so it is stable, and the picker in the create dialog is therefore currently
 *    cosmetic (see the open items in the work-package report).
 *  - option ids: the server's ids are `opt_` + 12 hex and become PostgreSQL enum labels,
 *    so an id the UI minted locally must NOT be sent. `toWireOptions` drops it and lets
 *    the server mint one; identity is preserved for options that already have a real id.
 *  - system fields are dropped from the field list the screens see (they are not
 *    editable, and two of them have no user-facing field type at all), but their IDS are
 *    needed to select `created_at` and friends in `POST /api/query`.
 */

/**
 * Object api-name keywords the sidebar can render at a glance. Cosmetic, stable.
 * First match wins, so the specific rows (creator, payment) sit above the
 * broad ones they would otherwise fall into. A junction keeps its engine name
 * `jt_<hex>`, which no keyword can match - hence the prefix rule.
 *
 * THE FIFTEEN SYMBOLS FIRST (symbol set v2, 2026-09-09): an object whose name says
 * what it is gets the symbol Arif approved for that object, and only a name none of
 * the fifteen fit falls through to a hairline icon. The seed writes the same names
 * into the column (D13), so a live instance and a fresh one agree.
 *
 * TWO ROWS LEFT THE HAIRLINE SET (redesign register A5-3, 2026-10-04: one symbol set for
 * every sidebar and settings row). The junction, "Agency members", takes the `members`
 * symbol the Members pane carries, and a message object - live, the Message templates the
 * Welcome Messages pane edits - takes our drawing of Apple's `message`
 * (`ui/ObjectGlyph/drawings.tsx`). Both drew a 14px hairline beside symbols that fill
 * their box.
 */
const ICON_BY_KEYWORD: readonly (readonly [RegExp, ObjectIcon])[] = [
  [/^jt_/, 'members'],
  [/agenc|company|compan|studio|brand/, 'agency'],
  [/creator/, 'creator'],
  [/team|staff|coach/, 'team'],
  [/customer|client|contact|person|people|member|user/, 'person'],
  [/payment|invoice|billing|installment/, 'payment'],
  [/deal|offer|contract|subscription/, 'deal'],
  [/lead|prospect/, 'lead'],
  [/enroll|cohort/, 'enrollment'],
  [/phase|transition/, 'phase'],
  [/pulse|weekly|health/, 'pulse'],
  [/win|award|trophy|case_stud/, 'win'],
  [/gameplan|plan|goal/, 'gameplan'],
  [/call|phone/, 'call'],
  [/deliver|submission|intake/, 'delivery'],
  [/mail|email|message/, 'message'],
  [/event|meeting|appointment|session/, 'calendar'],
  [/task|todo|ticket/, 'success'],
  [/note|doc/, 'info'],
];

export function iconForObject(name: string): ObjectIcon {
  const found = ICON_BY_KEYWORD.find(([pattern]) => pattern.test(name));
  return found?.[1] ?? 'table';
}

/**
 * AN ACTION'S SYMBOL IS NOT AN OBJECT'S HANDLE (fulfillment round 2, F8). The live Gameplans
 * object carried `icon: 'trash'` (`GET /api/metadata`, 2026-09-29) - a hairline name the
 * object picker offers among the others (`app/admin/IconPicker.tsx` maps all of `ICON_NAMES`),
 * and neither the seed (`gameplan`) nor any import script wrote it. Rendered as stored, the
 * sidebar, Settings > Sidebar and Objects and Fields drew a trash can beside "Gameplans",
 * which is the product's own delete symbol (`RecordRowMenu`, `TaskRowMenu`, the record's
 * Delete) standing on a row that deletes nothing. The stored value then counts as no choice
 * and the object gets what an unset icon gets: its own symbol when its name says what it is,
 * the generic table on the graphite tile when it does not. An object whose NAME is about
 * deletion keeps the symbol, because there the handle and the thing agree.
 */
const DELETION_ICONS: ReadonlySet<string> = new Set(['trash']);
const NAMED_FOR_DELETION = /trash|delet|discard|bin$/;

/**
 * THE JUNCTION'S SEEDED HAIRLINE IS THE SEED'S, NOT A CHOICE (redesign register A5-3,
 * 2026-10-04). `seed/model.ts` `JUNCTION_ICON` writes `link` onto the promoted junction
 * (`seed/apply.ts`), so every instance stores it and the keyword rule above never runs for it.
 * Read as no choice, the junction gets `members` like an unset one. The seed itself is not
 * changed here: a new value there makes a live `seed:ofmos` refuse the junction's stored icon
 * as somebody's (`isPreviousSeedValue` knows no junction), which is the coordinator's call.
 */
const SEEDED_JUNCTION_ICON = 'link';
const JUNCTION_NAME = /^jt_/;

/**
 * THE STORED ICON WINS (D13); the keyword heuristic is what answers when nothing is
 * stored.
 *
 * `icon` used to be derived from the api name alone, which made the picker in the
 * create dialog cosmetic - `updateObject` accepted an icon and discarded it. Now the
 * column exists, so a value somebody chose has to beat a guess about their spelling.
 *
 * An unknown NAME falls back rather than throwing: the column is free text on the
 * server, and a glyph this build does not ship must not blank the sidebar.
 */
export function resolveObjectIcon(name: string, stored: string | null | undefined): ObjectIcon {
  if (stored === null || stored === undefined) return iconForObject(name);
  if (DELETION_ICONS.has(stored) && !NAMED_FOR_DELETION.test(name)) return iconForObject(name);
  if (stored === SEEDED_JUNCTION_ICON && JUNCTION_NAME.test(name)) return iconForObject(name);
  if (isGlyphName(stored) || isDrawingName(stored)) return stored;
  if ((ICON_NAMES as readonly string[]).includes(stored)) return stored as ObjectIcon;
  return iconForObject(name);
}

function asTagTone(color: string | undefined): TagTone | undefined {
  return color !== undefined && (TAG_TONES as readonly string[]).includes(color)
    ? (color as TagTone)
    : undefined;
}

/**
 * Which fields reach a screen, per D8's visibility mode.
 *
 * TWO of the three filters are UNCONDITIONAL and must stay that way:
 *  - SYSTEM fields are not editable and two of them have no user-facing field type,
 *  - only the twelve user-facing types map at all (UUID / JSONB are system storage,
 *    and `toFieldMetadata` casts `type` on the strength of this check).
 *
 * The THIRD - `isHidden` - is the one D8 turns into a mode. It used to be an
 * unconditional drop, which is why `is_hidden` was reachable from the API and from
 * nowhere in the browser: the flag arrived on the wire and the mapping layer threw it
 * away before any screen could see it, so a hidden field could be created and never
 * un-hidden. Under `all` it comes through FLAGGED; under `visible` nothing changes for
 * any consumer that predates this.
 *
 * ARCHIVED fields (`deletedAt`) are dropped under `visible` for the same reason and
 * carried under `all`, which is what the Archived disclosure lists (D9).
 */
export function isDisplayableField(
  field: WireField,
  visibility: MetadataVisibility = 'visible',
): boolean {
  if (field.isSystem || !(FIELD_TYPES as readonly string[]).includes(field.type)) return false;
  if (visibility === 'all') return true;
  return !field.isHidden && field.deletedAt === null;
}

export function toFieldOptions(field: WireField): FieldOptions | undefined {
  const selectOptions: readonly SelectOptionDef[] | undefined =
    field.options === null
      ? undefined
      : field.options.map((option, index) => {
          const tone = asTagTone(option.color);
          return {
            id: option.id,
            label: option.label,
            ...(tone === undefined ? {} : { color: tone }),
            // The server's array order IS the display order; the UI wants it explicit.
            position: index + 1,
          };
        });

  const options: FieldOptions = {
    ...(selectOptions === undefined ? {} : { selectOptions }),
    ...(field.settings.relation === undefined ? {} : { relation: field.settings.relation }),
    ...(field.settings.defaultCurrencyCode === undefined
      ? {}
      : { defaultCurrencyCode: field.settings.defaultCurrencyCode }),
    ...(field.settings.phoneDefaultRegion === undefined
      ? {}
      : { phoneDefaultRegion: field.settings.phoneDefaultRegion }),
    ...(field.settings.isUnique === undefined ? {} : { isUnique: field.settings.isUnique }),
    /**
     * D12, and the same ripple identity-resolution.md 1.2 names: dropped on the way IN,
     * the field editor would show "Use as the record title" OFF for the field that IS
     * the title, and a Save would then turn it off for real - because `settings` is
     * REPLACED by the server, not merged.
     */
    ...(field.settings.isLabel === undefined ? {} : { isLabel: field.settings.isLabel }),
    ...(field.settings.identity === undefined ? {} : { identity: field.settings.identity }),
    /**
     * file-attachments.md D10, the same ripple one setting further: dropped here, the field
     * editor would show the count cap at its default for a field that has one, and a Save
     * would then write the default back - because `settings` is REPLACED, not merged.
     */
    ...(field.settings.maxFiles === undefined ? {} : { maxFiles: field.settings.maxFiles }),
    /**
     * inbound-intake.md D18, the same ripple again: dropped here, a document field would
     * render as one collapsed line on its own record page and a Save from the field editor
     * would write the setting away, because `settings` is REPLACED, not merged.
     */
    ...(field.settings.multiline === undefined ? {} : { multiline: field.settings.multiline }),
    /**
     * phase-b-coach.md D1 and D2, the same ripple twice more. Dropped here, a checkpoint
     * would lose its deadline on the next Save of the field it belongs to, and a computed
     * field would render as an ordinary editable row - which invites the MANUAL write the
     * record layer refuses.
     */
    ...(field.settings.checkpoint === undefined ? {} : { checkpoint: field.settings.checkpoint }),
    ...(field.settings.computed === undefined ? {} : { computed: field.settings.computed }),
    /**
     * DESIGN.md G2 (BR-RECORD), the same ripple once more: dropped here, the record header
     * would show no key facts at all, and a Save from the field editor would write the place
     * away, because `settings` is REPLACED, not merged.
     */
    ...(field.settings.keyFact === undefined ? {} : { keyFact: field.settings.keyFact }),
    /**
     * identity-resolution.md 1.2's ripple, on the two files that section does not list
     * because they are on the other side of the wire. Dropped here, the field editor
     * showed the identifier switch OFF for a field that IS an identifier - and an admin
     * who then pressed Save would have turned it off for real, deleting the alias set.
     */
  };
  return Object.keys(options).length === 0 ? undefined : options;
}

export function toFieldMetadata(field: WireField, position: number): FieldMetadata {
  const options = toFieldOptions(field);
  return {
    isHidden: field.isHidden,
    isArchived: field.deletedAt !== null,
    id: field.id,
    universalId: field.universalId,
    origin: field.origin,
    objectId: field.objectId,
    name: field.name,
    label: field.label,
    // Guarded by `isDisplayableField`; the cast is what the guard establishes.
    type: field.type as FieldMetadata['type'],
    ...(field.description === null ? {} : { description: field.description }),
    isSystem: field.isSystem,
    /**
     * The server has no per-field required flag yet (`FieldSpec` carries `isUnique` and
     * nothing else), so nothing is marked required rather than guessing. Reported as an
     * open item instead of being faked in the UI.
     */
    isRequired: false,
    position,
    // D8: null is UNGROUPED, which is a trailing section rather than an absence.
    ...(field.groupLabel === null ? {} : { groupLabel: field.groupLabel }),
    ...(options === undefined ? {} : { options }),
  };
}

/**
 * Display fields in the order the SERVER says (admin-area.md D7, open risk 3).
 *
 * This used to hand each field `index + 1` - a fiction, because nothing anywhere ordered
 * the array it was numbering. With a real `position` column the fiction had to go in the
 * same change that introduced the column, or a reorder would have been saved and then
 * ignored on the way back.
 *
 * The line above it in this file, which looks almost identical, must NOT be touched: it
 * numbers SELECT OPTIONS, whose order genuinely is the array's.
 */
export function toFieldList(
  fields: readonly WireField[],
  visibility: MetadataVisibility = 'visible',
): readonly FieldMetadata[] {
  return fields
    .filter((field) => isDisplayableField(field, visibility))
    .map((field) => toFieldMetadata(field, field.position));
}

export function toObjectMetadata(object: WireObject, fields: readonly WireField[]): ObjectMetadata {
  /**
   * The label field is resolved over the LIVE fields, system ones included, exactly as
   * the server resolves it for relation chips and search hits. "Live" is now enforced
   * below rather than assumed: since D9 this function is handed archived rows too.
   * An object with no TEXT field has none; the screens fall back to "Untitled".
   */
  const labelField = resolveLabelField(
    fields
      /**
       * LIVE FIELDS ONLY, and this filter is load-bearing as of this module.
       *
       * `shared/label-field.ts` states its own precondition - "soft-deleted fields are
       * never candidates; callers pass live fields only" - and both server callers obey
       * it by reading `metadata.fieldsOf`, which drops `deletedAt`. The browser used to
       * obey it by accident, because `GET /api/metadata` did not return archived fields
       * at all. D9 changed that route to `allFieldsOf` so the archived list has something
       * to restore, and this became the ONE caller passing archived rows in.
       *
       * The consequence was silent and total: archive the TEXT field named `name` while
       * another TEXT field exists, and the server titles records by the other one while
       * the browser titles them by the archived one - whose id `isDisplayableField` then
       * drops, so it is never selected, never in `values`, and every row, card, relation
       * chip and search hit renders "Untitled". Archive the only TEXT field and D12's
       * "this object has no record title" empty state never appears either, because the
       * screen still believes it has one.
       */
      .filter((field) => field.deletedAt === null)
      .map((field) => ({
        id: field.id,
        name: field.name,
        type: field.type,
        isSystem: field.isSystem,
        settings: field.settings,
      })),
  );

  return {
    id: object.id,
    universalId: object.universalId,
    origin: object.origin,
    name: object.name,
    labelSingular: object.label,
    labelPlural: object.labelPlural,
    ...(object.description === null ? {} : { description: object.description }),
    icon: resolveObjectIcon(object.name, object.icon),
    isSystem: object.isSystem,
    position: object.position,
    // D11: presentation, never visibility. Null means the trailing unlabelled group.
    ...(object.navGroup === null ? {} : { navGroup: object.navGroup }),
    /**
     * E9: an unknown name is dropped rather than carried, the same defence
     * `resolveObjectIcon` makes one line up. A colour the tokens have no
     * `--color-tile-<name>` for would paint nothing on a row with room for a tile,
     * and null already has a drawing - the graphite one.
     */
    color: isObjectColor(object.color) ? object.color : null,
    isHidden: object.isHidden,
    isArchived: object.deletedAt !== null,
    // INV-9: a junction is an object like any other, and the settings block is the
    // only thing on the wire that says so. D10's promotion is offered on this flag.
    isJunction: object.settings?.junction !== undefined,
    ...(object.settings?.junction === undefined ? {} : { junction: object.settings.junction }),
    labelIdentifierFieldId: labelField?.id ?? '',
  };
}

/**
 * UI options -> the metadata API's option input.
 *
 * A locally minted id (a slug like `at_risk`) is DROPPED: the server's ids are enum
 * labels and it validates them (`opt_` + 12 hex), so sending one would be refused. An id
 * that already looks like a server id is sent, which is what keeps a rename from
 * orphaning every row that stored the old value (field-types.md).
 */
export function toWireOptions(
  options: readonly SelectOptionDef[] | undefined,
): readonly { id?: string; label: string; color?: string }[] | undefined {
  if (options === undefined) return undefined;
  return [...options]
    .sort((a, b) => a.position - b.position)
    .map((option) => ({
      ...(isSelectOptionId(option.id) ? { id: option.id } : {}),
      label: option.label,
      ...(option.color === undefined ? {} : { color: option.color }),
    }));
}

// --- records ----------------------------------------------------------------

/**
 * A RELATION arrives as `{id, label}` from `POST /api/query` and as a bare id from the
 * single-record read; the UI stores the ID and resolves the label itself through the
 * canonical cache (`useRelationLabel`). Unwrapping here means one shape reaches the
 * field components.
 */
export function toFieldValue(raw: unknown): FieldValue {
  if (raw === undefined) return null;
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    const candidate = raw as { id?: unknown; label?: unknown };
    if (typeof candidate.id === 'string' && typeof candidate.label === 'string') {
      return candidate.id;
    }
  }
  return raw as FieldValue;
}

export function toValues(
  values: Readonly<Record<string, unknown>>,
  skipFieldIds: ReadonlySet<string> = new Set(),
): Record<string, FieldValue> {
  const mapped: Record<string, FieldValue> = {};
  for (const [fieldId, raw] of Object.entries(values)) {
    if (skipFieldIds.has(fieldId)) continue;
    mapped[fieldId] = toFieldValue(raw);
  }
  return mapped;
}

/**
 * THE HALF `toFieldValue` THROWS AWAY (D3), kept beside the values instead of inside
 * them.
 *
 * `POST /api/query` resolves relation labels once per relation FIELD, in SQL; the
 * browser unwrapped `{id, label}` to the bare id and then re-fetched every label one
 * record at a time. On a memberships panel, where each row points at a distinct
 * person by construction, that is one request per row.
 *
 * `undefined` rather than an empty object when nothing was resolved, so a single-record
 * read (which returns bare ids) is distinguishable from a query that resolved no labels
 * and the display layer knows when to fall back.
 */
export function toRelationLabels(
  values: Readonly<Record<string, unknown>>,
  skipFieldIds: ReadonlySet<string> = new Set(),
): Readonly<Record<string, string>> | undefined {
  let labels: Record<string, string> | undefined;
  for (const [fieldId, raw] of Object.entries(values)) {
    if (skipFieldIds.has(fieldId)) continue;
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const candidate = raw as { id?: unknown; label?: unknown };
    if (typeof candidate.id !== 'string' || typeof candidate.label !== 'string') continue;
    labels ??= {};
    labels[fieldId] = candidate.label;
  }
  return labels;
}

const UNKNOWN_ACTOR: Actor = { source: 'SYSTEM' };

export function toActor(actor: WireActor | null, displayName?: string | null): Actor {
  if (actor === null) return UNKNOWN_ACTOR;
  return {
    source: actor.source,
    ...(actor.userId === undefined ? {} : { userId: actor.userId }),
    ...(actor.context === undefined ? {} : { context: actor.context }),
    ...(displayName === undefined || displayName === null ? {} : { displayName }),
  };
}

/** Provenance, with the display name the UI shows next to a human lock (INV-5). */
export function toProvenance(
  provenance: WireRecord['system']['provenance'],
): RecordRow['provenance'] {
  const mapped: Record<string, RecordRow['provenance'][string]> = {};
  for (const [fieldId, entry] of Object.entries(provenance)) {
    // Resolved by the server on the wire, same as the timeline's actorName.
    const displayName = entry.displayName;
    mapped[fieldId] = {
      // A MANUAL console write carries no user; the LOCK still exists (record-write.ts).
      userId: entry.userId ?? '',
      ...(displayName === undefined ? {} : { displayName }),
      at: entry.at,
    };
  }
  return mapped;
}

export function toRecordRow(record: WireRecord): RecordRow {
  return {
    id: record.system.id,
    values: toValues(record.values),
    createdAt: record.system.createdAt,
    updatedAt: record.system.updatedAt,
    createdBy: toActor(record.system.createdBy, record.system.createdBy?.displayName),
    updatedBy: toActor(record.system.updatedBy, record.system.updatedBy?.displayName),
    provenance: toProvenance(record.system.provenance),
    // D21: the wire has carried this since WP3; dropping it here is what made an archived
    // record indistinguishable from a missing one everywhere above this line.
    deletedAt: record.system.deletedAt,
  };
}

// --- events -----------------------------------------------------------------

/**
 * The server's action vocabulary is the UI's event vocabulary minus the one kind only a
 * fixture can produce (`pending_approval`), so this map is total in one direction and
 * cannot silently mislabel: `soft_deleted` is the UI's `deleted`, everything else keeps
 * its name.
 *
 * TOTAL is the load-bearing word. The map is typed `Record<RecordEventAction, ...>`, so
 * widening the shared vocabulary 12 -> 23 (notes-tasks-comments.md 3.2) failed to compile
 * here until the eleven entries existed - which is how an action the timeline would have
 * rendered as `undefined` becomes a build error instead of a blank row.
 */
const EVENT_TYPE_BY_ACTION: Readonly<Record<RecordEventAction, RecordEventType>> = {
  created: 'created',
  updated: 'updated',
  soft_deleted: 'deleted',
  restored: 'restored',
  erased: 'erased',
  write_blocked: 'write_blocked',
  lock_released: 'lock_released',
  proposal_expired: 'proposal_expired',
  merged_from: 'merged_from',
  merged_into: 'merged_into',
  unmerged: 'unmerged',
  merge_repointed: 'merge_repointed',
  // Identity, deliberately: an annotation action means one thing and the renderer needs
  // that thing, so translating `task_completed` into a coarser UI kind would throw away
  // the distinction the action set was split to preserve.
  note_added: 'note_added',
  note_edited: 'note_edited',
  note_deleted: 'note_deleted',
  task_added: 'task_added',
  task_edited: 'task_edited',
  task_completed: 'task_completed',
  task_reopened: 'task_reopened',
  task_deleted: 'task_deleted',
  comment_added: 'comment_added',
  comment_edited: 'comment_edited',
  comment_deleted: 'comment_deleted',
};

/**
 * A `write_blocked` event stores the LOCK in the `before` slot, not the kept value
 * (write.ts `recordBlocked`), so the diff is rendered as "no kept value shown, this is
 * what the machine wanted" and the holder goes into the message. Inventing a kept value
 * here would be a lie in an audit trail.
 */
function toBlockedMessage(values: WireRecordEvent['values']): string | undefined {
  const first = Object.values(values?.fields ?? {})[0]?.before;
  if (typeof first !== 'object' || first === null) return undefined;
  const lock = first as { userId?: unknown; at?: unknown };
  const at = typeof lock.at === 'string' ? lock.at.slice(0, 10) : 'earlier';
  return `Refused: a person corrected this field on ${at}, so automated writers can't overwrite it.`;
}

/**
 * The message an approval leaves on the write it released (INV-10).
 *
 * The event's own actor stays the AGENT that proposed the change (pending-change.md rule
 * 1), so without this line the timeline would show an agent write with no hint that a
 * human let it through - and an edited approval would look like the agent got it right.
 */
function toApprovalMessage(approval: WireRecordEvent['approval']): string | undefined {
  if (approval === null || approval === undefined) return undefined;
  return approval.edited
    ? 'Approved by a human after editing the proposed values.'
    : 'Approved by a human as proposed.';
}

export function toRecordEvent(event: WireRecordEvent): RecordEvent {
  const blocked = event.action === 'write_blocked';
  /**
   * An expired proposal never touched the record, so its `before` is empty by
   * construction (write.ts) - the same shape as a refused write, and rendered the same
   * way: what the machine wanted, and nothing claimed about what the record held.
   */
  const expired = event.action === 'proposal_expired';
  const fields = Object.entries(event.values?.fields ?? {});
  const message = blocked
    ? toBlockedMessage(event.values)
    : expired
      ? 'Nobody decided this proposal within its window, so it expired and was never applied.'
      : toApprovalMessage(event.approval);
  /**
   * 3.3's annotation block, carried through UNTRANSLATED. It is already the UI's shape - a
   * kind, a row id and before/after diffs of prose - and the whole point of putting the
   * full body in the event was that the timeline can render a note card from what it
   * already holds. Dropping it here would have meant a request per timeline entry.
   */
  const annotation = event.values?.annotation;
  const reason = event.values?.reason;

  return {
    id: event.id,
    objectId: event.objectId,
    recordId: event.recordId,
    type: EVENT_TYPE_BY_ACTION[event.action],
    at: event.at,
    actor: toActor(event.actor, event.actorName),
    diff: fields.map(([fieldId, diff]) => ({
      fieldId,
      before: blocked || expired ? null : toFieldValue(diff.before),
      after: toFieldValue(diff.after),
    })),
    ...(message === undefined ? {} : { message }),
    ...(annotation === undefined ? {} : { annotation }),
    ...(reason === undefined ? {} : { reason }),
  };
}
